const LOOX_STOREFRONT_ORIGIN = 'https://storefront-api.loox.io';
const MAX_LOOX_REVIEWS = 12;
const PRODUCT_HOSTS = new Set([
  'hazybotanicals.com',
  'www.hazybotanicals.com',
  'hazybotanicals.myshopify.com',
  '1d50rm-qs.myshopify.com',
]);

/**
 * @param {string} publicStoreId
 * @param {number|string} [requestedLimit]
 */
export function buildLooxReviewsUrl(publicStoreId, requestedLimit = 10) {
  const storeId = String(publicStoreId || '').trim();
  const limit = Math.min(MAX_LOOX_REVIEWS, Math.max(1, Number.parseInt(String(requestedLimit), 10) || 10));
  const url = new URL(`/storefront/v1/store/${encodeURIComponent(storeId)}/product-reviews`, LOOX_STOREFRONT_ORIGIN);

  url.searchParams.set('sort', 'featured');
  url.searchParams.set('direction', 'desc');
  url.searchParams.set('limit', String(limit));

  return url.toString();
}

/**
 * @param {unknown} value
 * @param {(host: string) => boolean} allowedHost
 */
function approvedHttpsUrl(value, allowedHost) {
  try {
    const url = new URL(typeof value === 'string' ? value : '');
    return url.protocol === 'https:' && allowedHost(url.hostname) ? url.toString() : null;
  } catch {
    return null;
  }
}

/** @param {unknown} media */
function normalizeMedia(media) {
  if (!Array.isArray(media)) return null;

  for (const item of /** @type {Array<Record<string, unknown>>} */ (media)) {
    const mediaType = typeof item?.type === 'string' ? item.type : '';
    const type = mediaType === 'video' ? 'video' : ['image', 'photo'].includes(mediaType) ? 'image' : null;
    const url = approvedHttpsUrl(item?.url, (host) => host === 'loox.io' || host.endsWith('.loox.io'));
    if (type && url) return { type, url };
  }

  return null;
}

/** @param {unknown} product */
function normalizeProduct(product) {
  if (!product || typeof product !== 'object') return null;
  const candidate = /** @type {Record<string, unknown>} */ (product);
  const name = typeof candidate.name === 'string' ? candidate.name.trim() : '';
  const url = approvedHttpsUrl(candidate.url, (host) => PRODUCT_HOSTS.has(host));
  return name && url ? { name, url } : null;
}

/** @param {unknown} review */
export function normalizeLooxReview(review) {
  if (!review || typeof review !== 'object') return null;
  const candidate = /** @type {Record<string, any>} */ (review);
  const id = typeof candidate.id === 'string' ? candidate.id.trim() : '';
  const body = typeof candidate.body === 'string' ? candidate.body.trim() : '';
  const reviewerName = typeof candidate.reviewer?.name === 'string' ? candidate.reviewer.name.trim() : '';
  const rating = Number(candidate.rating);

  if (!id || !body || !reviewerName || !Number.isInteger(rating) || rating < 1 || rating > 5) return null;

  return {
    id,
    source: 'loox',
    rating,
    body,
    reviewerName,
    verified: candidate.verified === true,
    media: normalizeMedia(candidate.media),
    product: normalizeProduct(candidate.product),
  };
}

/**
 * @param {{ publicStoreId: string, limit?: number|string, fetchImpl?: typeof fetch, timeoutMs?: number }} options
 */
export async function fetchLooxReviews({
  publicStoreId,
  limit = 10,
  fetchImpl = globalThis.fetch,
  timeoutMs = 8000,
}) {
  if (!String(publicStoreId || '').trim()) throw new Error('Loox public store ID is required.');
  if (typeof fetchImpl !== 'function') throw new Error('Fetch is unavailable.');

  const controller = new AbortController();
  const timeout = globalThis.setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetchImpl(buildLooxReviewsUrl(publicStoreId, limit), {
      credentials: 'omit',
      signal: controller.signal,
    });
    if (!response.ok) throw new Error(`Loox Storefront API returned ${response.status || 'an error'}.`);

    const payload = await response.json();
    if (!payload || !Array.isArray(payload.reviews)) throw new Error('Loox Storefront API returned an invalid payload.');

    return payload.reviews.map(normalizeLooxReview).filter(Boolean);
  } finally {
    globalThis.clearTimeout(timeout);
  }
}

/** @param {typeof globalThis.matchMedia} [matchMedia] */
export function preferredScrollBehavior(matchMedia = globalThis.matchMedia) {
  return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
}

/**
 * @param {Element[]} cards
 * @param {number} currentIndex
 * @param {number} direction
 * @param {number} [tolerance]
 */
export function nextDistinctCardIndex(cards, currentIndex, direction, tolerance = 2) {
  if (cards.length < 2 || direction === 0) return currentIndex;
  const currentLeft = cards[currentIndex]?.getBoundingClientRect().left;
  if (typeof currentLeft !== 'number' || !Number.isFinite(currentLeft)) return currentIndex;

  for (let step = 1; step < cards.length; step += 1) {
    const candidateIndex = (currentIndex + Math.sign(direction) * step + cards.length) % cards.length;
    const candidateLeft = cards[candidateIndex]?.getBoundingClientRect().left;
    if (
      typeof candidateLeft === 'number' &&
      Number.isFinite(candidateLeft) &&
      Math.abs(candidateLeft - currentLeft) > tolerance
    ) {
      return candidateIndex;
    }
  }

  return currentIndex;
}

/** @param {string} tag @param {string} className */
function element(tag, className) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  return node;
}

/**
 * @param {NonNullable<ReturnType<typeof normalizeLooxReview>>} review
 */
function createLooxReviewCard(review) {
  const card = element('article', 'hb-reviews__card');
  card.dataset.reviewCard = '';
  card.dataset.reviewSource = 'loox';
  card.dataset.reviewHasMedia = review.media ? 'true' : 'false';
  card.dataset.looxCard = '';
  card.setAttribute('role', 'listitem');

  if (review.media) {
    const media = element('div', 'hb-reviews__media');
    if (review.media.type === 'video') {
      const video = /** @type {HTMLVideoElement} */ (element('video', 'hb-reviews__video'));
      video.src = review.media.url;
      video.controls = true;
      video.preload = 'metadata';
      video.playsInline = true;
      media.append(video);
    } else {
      const image = /** @type {HTMLImageElement} */ (element('img', 'hb-reviews__image'));
      image.src = review.media.url;
      image.alt = `Review photo from ${review.reviewerName}`;
      image.loading = 'lazy';
      image.decoding = 'async';
      media.append(image);
    }
    card.append(media);
  }

  const content = element('div', 'hb-reviews__content');
  const rating = element('div', 'hb-reviews__rating');
  rating.setAttribute('aria-label', `${review.rating} out of 5 stars`);
  for (let index = 1; index <= 5; index += 1) {
    const star = element('span', index > review.rating ? 'is-empty' : '');
    star.setAttribute('aria-hidden', 'true');
    star.textContent = '★';
    rating.append(star);
  }
  content.append(rating);

  const quote = element('blockquote', 'hb-reviews__quote');
  quote.textContent = review.body;
  content.append(quote);

  const footer = element('footer', 'hb-reviews__footer');
  const reviewer = element('cite', 'hb-reviews__reviewer');
  reviewer.textContent = review.reviewerName;
  footer.append(reviewer);

  const source = element('span', 'hb-reviews__source');
  source.textContent = review.verified ? 'Verified Loox review' : 'Loox review';
  footer.append(source);

  if (review.product) {
    const product = /** @type {HTMLAnchorElement} */ (element('a', 'hb-reviews__product'));
    product.href = review.product.url;
    product.textContent = review.product.name;
    footer.append(product);
  }

  content.append(footer);
  card.append(content);
  return card;
}

if (typeof window !== 'undefined' && 'customElements' in window && !customElements.get('hb-reviews-carousel')) {
  class HBReviewsCarousel extends HTMLElement {
    connectedCallback() {
      if (this.dataset.initialized === 'true') return;
      this.dataset.initialized = 'true';

      this.viewport = this.querySelector('[data-reviews-viewport]');
      this.track = this.querySelector('[data-reviews-track]');
      this.controls = this.querySelector('[data-reviews-controls]');
      this.current = this.querySelector('[data-reviews-current]');
      this.total = this.querySelector('[data-reviews-total]');
      this.status = this.querySelector('[data-reviews-status]');

      this.querySelector('[data-reviews-previous]')?.addEventListener('click', () => this.move(-1));
      this.querySelector('[data-reviews-next]')?.addEventListener('click', () => this.move(1));
      this.viewport?.addEventListener('scroll', () => this.updatePosition(), { passive: true });
      this.addEventListener('shopify:block:select', (event) => this.selectEditorBlock(event));

      this.load();
    }

    async load() {
      const publicStoreId = this.dataset.looxPublicStoreId?.trim();
      if (!publicStoreId || !this.track) {
        this.finalize('disabled');
        return;
      }

      try {
        const track = this.track;
        const reviews = await fetchLooxReviews({
          publicStoreId,
          limit: this.dataset.looxLimit || 10,
        });
        const firstManualReview = track.querySelector('[data-review-source="custom"]');
        for (const review of reviews) {
          track.insertBefore(createLooxReviewCard(review), firstManualReview);
        }
        this.viewport?.scrollTo({ left: 0, behavior: 'auto' });
        if (this.status) this.status.textContent = `${reviews.length} Loox reviews loaded.`;
        this.finalize('loaded');
      } catch (error) {
        console.warn('Hazy reviews carousel could not load Loox reviews.', error);
        this.finalize('degraded');
      }
    }

    /** @param {string} state */
    finalize(state) {
      this.dataset.looxState = state;
      this.classList.remove('hb-reviews--pending-empty');
      const cards = this.cards;
      this.classList.toggle('hb-reviews--empty', cards.length === 0);
      this.controls?.toggleAttribute('hidden', cards.length < 2);
      if (this.total) this.total.textContent = String(cards.length || 1);
      this.updatePosition();
    }

    get cards() {
      return [...this.querySelectorAll('[data-review-card]')];
    }

    /** @param {number} direction */
    move(direction) {
      const cards = this.cards;
      if (!this.viewport || cards.length < 2) return;
      const currentIndex = this.currentIndex();
      const nextIndex = nextDistinctCardIndex(cards, currentIndex, direction);
      cards[nextIndex]?.scrollIntoView({
        behavior: preferredScrollBehavior(),
        block: 'nearest',
        inline: 'start',
      });
    }

    currentIndex() {
      if (!this.viewport || this.cards.length === 0) return 0;
      const viewportLeft = this.viewport.getBoundingClientRect().left;
      let closestIndex = 0;
      let closestDistance = Number.POSITIVE_INFINITY;
      this.cards.forEach((card, index) => {
        const distance = Math.abs(card.getBoundingClientRect().left - viewportLeft);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = index;
        }
      });
      return closestIndex;
    }

    updatePosition() {
      if (this.current) this.current.textContent = String(this.currentIndex() + 1);
    }

    /** @param {Event} event */
    selectEditorBlock(event) {
      if (!(event.target instanceof Element)) return;
      event.target.closest('[data-review-card]')?.scrollIntoView({
        behavior: preferredScrollBehavior(),
        block: 'nearest',
        inline: 'center',
      });
    }
  }

  customElements.define('hb-reviews-carousel', HBReviewsCarousel);
}
